import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

type Coords = { top: number; left: number; width: number }

type Props = {
	label: string
	value: string
	onChange: (next: string) => void
	options: string[]
	placeholder?: string
}

export function PositionCombobox({ label, value, onChange, options, placeholder = 'Выберите или введите' }: Props) {
	const uid = useId()
	const triggerId = `${uid}-trigger`
	const rootRef = useRef<HTMLDivElement | null>(null)
	const triggerRef = useRef<HTMLButtonElement | null>(null)
	const popoverRef = useRef<HTMLDivElement | null>(null)
	const searchRef = useRef<HTMLInputElement | null>(null)
	const [open, setOpen] = useState(false)
	const [filter, setFilter] = useState('')
	const [coords, setCoords] = useState<Coords>({ top: 0, left: 0, width: 280 })

	const mergedOptions = useMemo(() => {
		const v = value.trim()
		const set = new Set<string>()
		for (const o of options) {
			const t = o.trim()
			if (t !== '') {
				set.add(t)
			}
		}
		if (v !== '' && !set.has(v)) {
			set.add(v)
		}
		return [...set].sort((a, b) => a.localeCompare(b, 'ru'))
	}, [options, value])

	const filtered = useMemo(() => {
		const q = filter.trim().toLowerCase()
		if (q === '') {
			return mergedOptions
		}
		return mergedOptions.filter((o) => o.toLowerCase().includes(q))
	}, [filter, mergedOptions])

	const customSuggestion = useMemo(() => {
		const t = filter.trim()
		if (t === '') {
			return null
		}
		const exact = mergedOptions.some((o) => o.toLowerCase() === t.toLowerCase())
		return exact ? null : t
	}, [filter, mergedOptions])

	const updatePopoverPosition = useCallback(() => {
		const tr = triggerRef.current
		if (!tr) {
			return
		}
		const rect = tr.getBoundingClientRect()
		const margin = 8
		const w = Math.min(Math.max(rect.width, 260), window.innerWidth - 2 * margin)
		let left = rect.left
		if (left + w > window.innerWidth - margin) {
			left = Math.max(margin, window.innerWidth - w - margin)
		}
		if (left < margin) {
			left = margin
		}
		let top = rect.bottom + 4
		const pop = popoverRef.current
		if (pop) {
			const ph = pop.getBoundingClientRect().height
			if (top + ph > window.innerHeight - margin) {
				const above = rect.top - ph - 4
				if (above >= margin) {
					top = above
				}
			}
		}
		setCoords({ top, left, width: w })
	}, [])

	useLayoutEffect(() => {
		if (!open) {
			return
		}
		updatePopoverPosition()
		const idRaf = requestAnimationFrame(() => updatePopoverPosition())
		return () => cancelAnimationFrame(idRaf)
	}, [open, updatePopoverPosition, filtered.length])

	useEffect(() => {
		if (!open) {
			return
		}
		const onScrollOrResize = () => updatePopoverPosition()
		window.addEventListener('resize', onScrollOrResize)
		window.addEventListener('scroll', onScrollOrResize, true)
		return () => {
			window.removeEventListener('resize', onScrollOrResize)
			window.removeEventListener('scroll', onScrollOrResize, true)
		}
	}, [open, updatePopoverPosition])

	useEffect(() => {
		if (!open) {
			return
		}
		const onDoc = (e: MouseEvent) => {
			const t = e.target
			if (!(t instanceof Node)) {
				return
			}
			if (rootRef.current?.contains(t)) {
				return
			}
			if (popoverRef.current?.contains(t)) {
				return
			}
			setOpen(false)
		}
		document.addEventListener('mousedown', onDoc)
		return () => document.removeEventListener('mousedown', onDoc)
	}, [open])

	useEffect(() => {
		if (open) {
			setFilter(value)
			requestAnimationFrame(() => searchRef.current?.focus())
		}
	}, [open, value])

	const displayText = value.trim() !== '' ? value : placeholder

	const selectOption = (t: string) => {
		onChange(t)
		setOpen(false)
	}

	return (
		<div ref={rootRef} className="position-combobox-root">
			<label className="position-combobox-label" htmlFor={triggerId}>
				{label}
			</label>
			<button
				ref={triggerRef}
				id={triggerId}
				type="button"
				className="position-combobox-trigger"
				onClick={() => setOpen((v) => !v)}
				aria-haspopup="listbox"
				aria-expanded={open}
			>
				<span className={`position-combobox-trigger-text${value.trim() === '' ? ' is-placeholder' : ''}`}>{displayText}</span>
				<span className="position-combobox-trigger-caret" aria-hidden="true">
					▾
				</span>
			</button>

			{open &&
				createPortal(
					<div
						ref={popoverRef}
						className="position-combobox-popover position-combobox-popover-portal"
						style={{ top: coords.top, left: coords.left, width: coords.width }}
						role="listbox"
					>
						<div className="position-combobox-search-wrap">
							<input
								ref={searchRef}
								type="text"
								className="position-combobox-search"
								value={filter}
								onChange={(e) => setFilter(e.target.value)}
								placeholder="Поиск или новая должность"
								autoComplete="off"
							/>
						</div>
						<div className="position-combobox-list">
							{customSuggestion ? (
								<button
									type="button"
									className="position-combobox-item position-combobox-item-custom"
									onClick={() => selectOption(customSuggestion)}
								>
									Использовать «{customSuggestion}»
								</button>
							) : null}
							{filtered.length === 0 && !customSuggestion ? (
								<div className="position-combobox-empty muted">Нет совпадений</div>
							) : null}
							{filtered.map((o) => (
								<button key={o} type="button" className="position-combobox-item" onClick={() => selectOption(o)}>
									{o}
								</button>
							))}
						</div>
					</div>,
					document.body
				)}
		</div>
	)
}
